<script setup>
import { ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import ButtonV4 from 'next/button/Button.vue';
import axios from 'axios';

const template = ref(
  'Olá {{nome}}, sua fatura no valor de R$ {{valor}} vence hoje!'
);
const testData = ref('{\n  "nome": "Bruno",\n  "valor": "99,90"\n}');
const selectedChannel = ref('sms');
const testPhoneNumber = ref('+5511999999999');
const generatedPreview = ref('');
const isTesting = ref(false);

const testTemplate = async () => {
  isTesting.value = true;
  try {
    let variables = {};
    try {
      variables = JSON.parse(testData.value);
    } catch (e) {
      useAlert('Erro no JSON de teste. Verifique o formato.');
      isTesting.value = false;
      return;
    }

    const response = await axios.post(
      '/api/v1/accounts/1/automations/send_message',
      {
        template: template.value,
        variables: variables,
        phone_number: testPhoneNumber.value,
        channel: selectedChannel.value,
        inbox_id: 1, // dummy for whatsapp testing
      }
    );

    generatedPreview.value = response.data.content;
    useAlert('Mensagem testada com sucesso!');
  } catch (error) {
    useAlert(
      'Erro ao testar mensagem: ' +
        (error.response?.data?.error || error.message)
    );
  } finally {
    isTesting.value = false;
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        title="Automações (API Inbound)"
        description="Crie templates de mensagens com variáveis dinâmicas e integre seu ERP para disparar mensagens automáticas via WhatsApp ou LocaSMS."
        feature-name="automations"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6 w-full max-w-4xl mx-auto pb-12 pt-4">
        <div
          class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6"
        >
          <h3
            class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4"
          >
            Template da Mensagem
          </h3>
          <p class="text-sm text-slate-500 mb-4">
            Use chaves duplas para criar variáveis. Exemplo:
            <code>{{ nome }}</code>
          </p>
          <textarea
            v-model="template"
            class="w-full h-32 p-4 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 font-mono"
            placeholder="Digite sua mensagem com variáveis..."
          />
        </div>

        <div
          class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6"
        >
          <h3
            class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4"
          >
            Área de Testes
          </h3>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label
                class="block text-sm text-slate-600 dark:text-slate-400 mb-1"
                >Variáveis de Teste (JSON)</label
              >
              <textarea
                v-model="testData"
                class="w-full h-32 p-3 border border-slate-300 dark:border-slate-700 rounded-lg font-mono text-sm"
              />
            </div>
            <div>
              <label
                class="block text-sm text-slate-600 dark:text-slate-400 mb-1"
                >Telefone Destino</label
              >
              <input
                v-model="testPhoneNumber"
                type="text"
                class="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg mb-4"
              />

              <label
                class="block text-sm text-slate-600 dark:text-slate-400 mb-1"
                >Canal</label
              >
              <select
                v-model="selectedChannel"
                class="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg mb-4"
              >
                <option value="sms">SMS (LocaSMS)</option>
                <option value="whatsapp">WhatsApp</option>
              </select>

              <ButtonV4
                class="w-full"
                :is-loading="isTesting"
                @click="testTemplate"
              >
                Testar Disparo
              </ButtonV4>
            </div>
          </div>

          <div
            v-if="generatedPreview"
            class="mt-4 p-4 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-lg"
          >
            <h4 class="font-bold text-emerald-800 dark:text-emerald-400 mb-2">
              Mensagem Processada:
            </h4>
            <p class="text-emerald-700 dark:text-emerald-300">
              {{ generatedPreview }}
            </p>
          </div>
        </div>

        <div class="bg-slate-800 rounded-xl p-6 text-white">
          <h3 class="text-lg font-semibold mb-4">Integração ERP (CURL)</h3>
          <p class="text-sm text-slate-300 mb-4">
            Entregue este código ao seu desenvolvedor:
          </p>
          <pre
            class="bg-black p-4 rounded-lg overflow-x-auto text-sm text-emerald-400"
          >
curl -X POST https://app.lumeratec.com/api/v1/accounts/YOUR_ACCOUNT_ID/automations/send_message \
  -H "api_access_token: YOUR_TOKEN" \
  -H "Content-Type: application/json" \
  -d '{
    "channel": "sms",
    "phone_number": "+5511999999999",
    "template": "Olá {{ nome }}, sua fatura no valor de R$ {{
              valor
            }} vence hoje!",
    "variables": {
      "nome": "Bruno",
      "valor": "99,90"
    }
  }'</pre
          >
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
